// Shared detector for the "profile is regenerating" 409 the backend gate raises
// (app/api/utils/profile_regen_gate.py). Profile-consuming actions — scan/match,
// application + answer generation, intro/sponsorship letters — are blocked while a
// background regen runs so their output isn't built on a profile about to change.
//
// The 409 carries a stable `code` so it can be told apart from other 409s (e.g.
// duplicate-org). Consumer API calls run `throwIfProfileRegenerating` on a non-ok
// response so the typed error surfaces ahead of their generic error handling.

export const PROFILE_REGEN_CODE = 'profile_regenerating'

// in_progress → "please wait"; failed → "press Update profile to retry".
export type ProfileRegenState = 'in_progress' | 'failed'

export class ProfileRegeneratingError extends Error {
  readonly state: ProfileRegenState
  // For state="failed": why it failed ("insufficient_data" | "error" | null),
  // so the block dialog can tailor its message + recovery action.
  readonly reason: string | null

  constructor(state: ProfileRegenState, message: string, reason: string | null) {
    super(message)
    this.name = 'ProfileRegeneratingError'
    this.state = state
    this.reason = reason
  }
}

export function isProfileRegeneratingError(
  error: unknown,
): error is ProfileRegeneratingError {
  return error instanceof ProfileRegeneratingError
}

// Throw a typed error if `response` is the profile-regen 409; otherwise return so
// the caller continues its own error handling. Reads a clone so the caller can
// still consume the body. Call this before a generic non-ok throw.
export async function throwIfProfileRegenerating(
  response: Response,
): Promise<void> {
  if (response.status !== 409) return
  let detail: unknown
  try {
    detail = (await response.clone().json())?.detail
  } catch {
    return // not JSON — let the caller handle it
  }
  if (
    detail &&
    typeof detail === 'object' &&
    (detail as { code?: string }).code === PROFILE_REGEN_CODE
  ) {
    const { state, message, reason } = detail as {
      state: ProfileRegenState
      message: string
      reason?: string | null
    }
    throw new ProfileRegeneratingError(state, message, reason ?? null)
  }
}
